import classnames from 'classnames';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { IconButton } from '#src/atoms/buttons/IconButton/index.tsx';
import { iconChevronLeft } from '#src/atoms/icons/system/iconChevronLeft.ts';
import { iconChevronRight } from '#src/atoms/icons/system/iconChevronRight.ts';
import { useTouchScreen } from '#src/hooks/device/useTouchScreen.ts';
import { useIsOverflowing } from '#src/hooks/ui/useIsOverflowing.ts';
import { useScrollPosition } from '#src/hooks/ui/useScrollPosition.ts';
import { glossary } from '#src/lib/glossary.ts';
import { unitMapping } from '#src/lib/style.ts';
import { tabletAndAbove } from '#src/style/mediaQuery.ts';

// oxlint-disable-next-line sorare/use-horizontal-vertical-layout
const ScrollButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  inset: 0 calc(-1 * var(--double-unit));
  pointer-events: none;
  z-index: 2;
  > * {
    pointer-events: auto;
    &[disabled] {
      visibility: hidden;
    }
  }
  &.contained {
    inset: 0 var(--contained-size);
  }
`;

const Wrapper = styled.div`
  position: relative;
  isolation: isolate;

  &.hideButtonsOnMobile {
    ${ScrollButtonWrapper} {
      visibility: hidden;
    }
  }
  &.grow {
    width: 100%;
  }

  @media ${tabletAndAbove} {
    ${ScrollButtonWrapper} {
      visibility: visible;
    }

    &.displayOnlyOnHover {
      ${ScrollButtonWrapper} {
        visibility: hidden;
      }
      &:hover,
      &:focus-within {
        ${ScrollButtonWrapper} {
          visibility: visible;
        }
      }
    }
  }
`;

type Props = {
  children: ReactNode;
  wrapperRef: RefObject<HTMLDivElement | null>;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  contained?: boolean | keyof typeof unitMapping | string;
  hideButtonsOnMobile?: boolean;
  displayOnlyOnHover?: boolean;
  behavior?: 'smooth' | 'instant' | 'auto';
  itemGap?: keyof typeof unitMapping;
  overhangSize?: keyof typeof unitMapping;
  scrollStep?: 'item' | 'container';
  grow?: boolean;
};

export const WithScrollButtons = ({
  children,
  wrapperRef,
  scrollContainerRef,
  contained,
  hideButtonsOnMobile = false,
  displayOnlyOnHover = true,
  itemGap = 2,
  overhangSize = 0,
  behavior = 'smooth',
  scrollStep = 'container',
  grow = false,
}: Props) => {
  // update state to force isOverflowing to recompute when the wrapperRef change
  const [, setUpdate] = useState<unknown>();
  const isOverflowing = useIsOverflowing(scrollContainerRef);
  const { scrollPosition } = useScrollPosition(scrollContainerRef);
  const { formatMessage } = useIntl();
  const isTouchScreen = useTouchScreen();

  // On IOS safari, the onMouseEnter/focus is triggered by first click. Prevetting the click on caroussel item.
  // https://stackoverflow.com/questions/46034186/ios-safari-click-event-not-triggerd-when-there-is-an-event-listener-on-mouse
  const actualDisplayOnlyOnHover = !isTouchScreen && displayOnlyOnHover;

  const getScrollValue = () => {
    const itemWidth = scrollContainerRef.current?.children[0]?.clientWidth || 0;
    const wrapperWidth = wrapperRef.current?.clientWidth || 0;

    const itemVisibleCount = Math.ceil(
      wrapperWidth / (itemWidth + 8 * itemGap)
    );

    const itemStepWidth = itemWidth + 8 * itemGap;
    const scrollValue =
      scrollStep === 'item'
        ? itemStepWidth
        : itemStepWidth * itemVisibleCount - 8 * overhangSize;
    return scrollValue;
  };

  const getContainedSize = () => {
    if (!contained) {
      return undefined;
    }
    if (typeof contained === 'boolean') {
      return 'var(--unit)';
    }
    if (typeof contained === 'string') {
      return contained;
    }
    return unitMapping[contained];
  };
  return (
    <Wrapper
      ref={wrapperRef}
      onMouseEnter={actualDisplayOnlyOnHover ? setUpdate : undefined}
      className={classnames({
        hideButtonsOnMobile,
        displayOnlyOnHover: actualDisplayOnlyOnHover,
        grow,
      })}
    >
      {children}
      {!!(isOverflowing || !displayOnlyOnHover) && (
        <ScrollButtonWrapper
          className={classnames({
            contained: !!contained,
          })}
          style={
            {
              '--contained-size': getContainedSize(),
            } as CSSProperties
          }
        >
          <IconButton
            icon={iconChevronLeft}
            color="tertiary"
            size="small"
            disabled={scrollPosition === 'start'}
            disableDebounce
            aria-label={formatMessage(glossary.previous)}
            onClick={evt => {
              evt.preventDefault();
              scrollContainerRef.current?.scrollBy({
                left: -getScrollValue(),
                behavior,
              });
            }}
          />
          <IconButton
            icon={iconChevronRight}
            color="tertiary"
            size="small"
            disabled={scrollPosition === 'end'}
            disableDebounce
            aria-label={formatMessage(glossary.next)}
            onClick={evt => {
              evt.preventDefault();
              scrollContainerRef.current?.scrollBy({
                left: getScrollValue(),
                behavior,
              });
            }}
          />
        </ScrollButtonWrapper>
      )}
    </Wrapper>
  );
};
